import * as React from 'react';
import { PlusCircle } from 'lucide-react';
import HelpTooltip from '@/components/ui/help-tooltip';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

interface FilterProps {
  title?: string;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  selected: string[];
  setSelected: (value: string[]) => void;
  tooltip?: string;
}

export const Filter = ({
  title,
  options,
  selected = [],
  setSelected,
  tooltip,
}: FilterProps) => {
  const trigger = (
    <div className='relative'>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='h-9'>
          <PlusCircle />
          {title}
          {selected?.length > 0 && (
            <>
              <Separator orientation='vertical' className='mx-2 h-4' />
              <Badge
                variant='secondary'
                className='rounded-sm px-1 font-normal lg:hidden'
              >
                {selected.length}
              </Badge>
              <div className='hidden space-x-1 lg:flex'>
                <Badge
                  variant='secondary'
                  className='rounded-sm px-1 font-normal'
                >
                  {selected.length} selected
                </Badge>
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
    </div>
  );

  return (
    <Popover>
      {tooltip ? (
        <HelpTooltip label={`${title} help`} trigger={trigger}>
          {tooltip}
        </HelpTooltip>
      ) : (
        trigger
      )}
      <PopoverContent className='m-w-[500px] w-fit p-0' align='start'>
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            {selected.length > 0 && (
              <>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => setSelected([])}
                    className='justify-center text-center'
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const newSelected: string[] = [...selected];

                      if (isSelected) {
                        const filteredSelected = newSelected.filter(
                          (value) => value !== option.value,
                        );
                        setSelected(filteredSelected);
                      } else {
                        newSelected.push(option.value);
                        setSelected(newSelected);
                      }
                    }}
                  >
                    <Checkbox checked={isSelected} />
                    {option.icon && (
                      <option.icon className='mr-2 h-4 w-4 text-muted-foreground' />
                    )}
                    <span>{option.value}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
